import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Sparkles, Send, Lightbulb, Bot, User } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { PageIntro, PanelHead } from "../components/UI";

const SUGGESTIONS = [
  "Item apa saja yang stoknya menipis?",
  "Berapa total nilai stok saat ini?",
  "Ada berapa PR yang masih menunggu persetujuan?",
  "Item mana yang perlu segera dibuatkan PR?",
];

export default function AiAssistantPage() {
  const [sessionId] = useState(() => `sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [insightsBusy, setInsightsBusy] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/assistant/chat", { session_id: sessionId, message: msg });
      setMessages((m) => [...m, { role: "assistant", content: data.answer }]);
    } catch (err) {
      const detail = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      toast.error(detail);
      setMessages((m) => [...m, { role: "assistant", content: `⚠ ${detail}`, error: true }]);
    } finally {
      setBusy(false);
    }
  };

  const getInsights = async () => {
    if (insightsBusy) return;
    setInsightsBusy(true);
    try {
      const { data } = await api.post("/assistant/insights");
      setMessages((m) => [...m, { role: "assistant", content: data.insights, insight: true }]);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setInsightsBusy(false);
    }
  };

  return (
    <>
      <PageIntro
        eyebrow="Administration · AI"
        title="AI Assistant"
        subtitle="Tanya apa saja tentang stok, PR, PO, dan receiving — dijawab Claude berdasarkan data aplikasi terkini."
        testid="assistant-title"
      />
      <section className="panel">
        <PanelHead title="Claude inventory assistant" detail="Powered by Claude Opus 5.5" />
        <div className="toolbar">
          <button className="secondary-button" data-testid="assistant-insights-button" onClick={getInsights} disabled={insightsBusy}>
            <Lightbulb size={15} /> {insightsBusy ? "Menganalisis..." : "Insight stok hari ini"}
          </button>
        </div>

        <div className="assistant-chat" data-testid="assistant-chat">
          {messages.length === 0 && (
            <div className="assistant-empty">
              <Sparkles size={26} />
              <p>Mulai dengan pertanyaan, atau coba salah satu contoh:</p>
              <div className="assistant-suggestions">
                {SUGGESTIONS.map((s) => (
                  <button key={s} type="button" className="chip" onClick={() => send(s)} data-testid="assistant-suggestion">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`chat-row ${m.role}`} data-testid={`chat-msg-${m.role}-${i}`}>
              <div className="chat-avatar">{m.role === "user" ? <User size={15} /> : <Bot size={15} />}</div>
              <div className={`chat-bubble ${m.error ? "error" : ""} ${m.insight ? "insight" : ""}`}>
                {m.content.split("\n").map((line, j) => <p key={j}>{line || "\u00a0"}</p>)}
              </div>
            </div>
          ))}
          {busy && (
            <div className="chat-row assistant">
              <div className="chat-avatar"><Bot size={15} /></div>
              <div className="chat-bubble"><span className="typing">Claude sedang mengetik…</span></div>
            </div>
          )}
          <div ref={endRef} />
        </div>

        <form
          className="assistant-input"
          onSubmit={(e) => { e.preventDefault(); send(); }}
        >
          <textarea
            data-testid="assistant-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder="Tanya tentang stok, PR, PO… (Enter untuk kirim)"
            rows={2}
          />
          <button type="submit" className="primary-button" data-testid="assistant-send" disabled={busy || !input.trim()}>
            <Send size={16} /> Kirim
          </button>
        </form>
      </section>
    </>
  );
}
